import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import type { ChartConfig, ChartType, MetricKey, AggregationBy, ChartMode } from '../../type/dynamicChart';
import { getMetricLabelKey } from '../../utils/chartDefaults';
import { metricRegistry } from '../../registry/metrics';
import { aggregationRegistry, type AggregationKey } from '../../registry/aggregations';
import { buildXMetrics, buildAllXMetrics, buildHistogramXMetrics, buildYMetrics } from '../../utils/chartConfigUtils';
import { chartTypeRegistry } from '../../registry/chartTypes';
import { OptionFieldRenderer } from './OptionFieldRenderer';
import { Checkbox } from '@/components/ui/checkbox';

type ChartConfigurationProps = {
  config: ChartConfig;
  onUpdate: (updates: Partial<ChartConfig>) => void;
  mode?: ChartMode;
};

// ── Reusable sub-components ────────────────────────────────────────────────

type LabeledSelectProps<T extends string> = {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string }>;
};

function LabeledSelect<T extends string>({ label, value, onChange, options }: LabeledSelectProps<T>) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type AggregationLevelSelectorProps = {
  value: AggregationBy;
  onChange: (value: AggregationBy) => void;
  mode?: ChartMode;
};

function AggregationLevelSelector({ value, onChange, mode = 'analytics' }: AggregationLevelSelectorProps) {
  const { t } = useTranslation();
  return (
    <LabeledSelect
      label={t('analytics.dashboard.complexCharts.configuration.aggregationLevel')}
      value={value}
      onChange={onChange}
      options={[
        { value: 'tool' as AggregationBy, label: t('analytics.dashboard.complexCharts.configuration.byTool') },
        { value: 'trace' as AggregationBy, label: t('analytics.dashboard.complexCharts.configuration.byTrace') },
        ...(mode === 'comparison'
          ? [{ value: 'reference' as AggregationBy, label: t('analytics.dashboard.complexCharts.configuration.byReference') }]
          : []),
      ]}
    />
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function ChartConfiguration({ config, onUpdate, mode = 'analytics' }: ChartConfigurationProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { t } = useTranslation();

  // Chart type options from registry
  const chartTypes = chartTypeRegistry.map((ct) => ({
    value: ct.key as ChartType,
    label: t(ct.labelKey),
  }));

  // Current chart type definition from registry
  const chartTypeDef = chartTypeRegistry.find((ct) => ct.key === config.type);

  // Metric lists (registry-driven)
  const xMetrics = buildXMetrics(mode).map((m) => ({ value: m.value, label: t(m.label) }));
  const allXMetrics = buildAllXMetrics(mode).map((m) => ({ value: m.value, label: t(m.label) }));
  const histogramXMetrics = buildHistogramXMetrics(mode).map((m) => ({ value: m.value, label: t(m.label) }));
  const yMetrics = buildYMetrics().map((m) => ({ value: m.value, label: t(m.label) }));

  const handleXMetricChange = (metric: MetricKey) => {
    onUpdate({ xAxis: { ...config.xAxis, metric, label: t(getMetricLabelKey(metric)) } });
  };

  // Aggregation select — visible only for numeric Y metrics when chart is not boxplot
  const yMetricDef = metricRegistry.find((m) => m.key === config.yAxis.metric);
  const showAggregationSelect = yMetricDef?.kind === 'numeric' && config.type !== 'boxplot';

  const aggregationOptions = aggregationRegistry
    .filter((a) => a.key !== 'mean' && (yMetricDef?.supportedAggregations.includes(a.key) ?? false))
    .map((a) => ({ value: a.key as AggregationKey, label: t(a.labelKey) }));

  const showAggLevelForXAxis = config.xAxis.metric === 'toolId';

  // Determines which chart types show an aggregation level selector
  const showAggLevel =
    ((config.type === 'scatter' || config.type === 'bar') && showAggLevelForXAxis) ||
    config.type === 'histogram' ||
    (config.type === 'pie' && showAggLevelForXAxis) ||
    (config.type === 'radar' && showAggLevelForXAxis) ||
    (config.type === 'boxplot' && showAggLevelForXAxis);

  return (
    <div className="space-y-4 border-t p-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Chart Type — registry-driven */}
        <LabeledSelect
          label={t('analytics.dashboard.complexCharts.configuration.chartType')}
          value={config.type}
          onChange={(value) => onUpdate({ type: value as ChartType })}
          options={chartTypes}
        />

        {/* X Axis (scatter, bar) — numeric + categorical metrics */}
        {(config.type === 'scatter' || config.type === 'bar') && (
          <LabeledSelect
            label={t('analytics.dashboard.complexCharts.configuration.xAxis')}
            value={config.xAxis.metric}
            onChange={(v) => handleXMetricChange(v as MetricKey)}
            options={allXMetrics}
          />
        )}

        {/* X Axis (histogram) */}
        {config.type === 'histogram' && (
          <LabeledSelect
            label={t('analytics.dashboard.complexCharts.configuration.xAxis')}
            value={config.xAxis.metric}
            onChange={(v) => handleXMetricChange(v as MetricKey)}
            options={histogramXMetrics}
          />
        )}

        {/* Group By (pie, radar, boxplot) */}
        {(config.type === 'pie' || config.type === 'radar' || config.type === 'boxplot') && (
          <LabeledSelect
            label={t('analytics.dashboard.complexCharts.configuration.groupBy')}
            value={config.xAxis.metric}
            onChange={(v) => handleXMetricChange(v as MetricKey)}
            options={xMetrics}
          />
        )}

        {/* Y Axis — all numeric metrics */}
        <LabeledSelect
          label={t('analytics.dashboard.complexCharts.configuration.yAxis')}
          value={config.yAxis.metric}
          onChange={(v) => {
            const metric = v as MetricKey;
            const newMetricDef = metricRegistry.find((m) => m.key === metric);
            const currentAgg = config.yAxis.aggregation;
            const newAgg =
              currentAgg && newMetricDef?.supportedAggregations.includes(currentAgg)
                ? currentAgg
                : (newMetricDef?.defaultAggregation ?? 'avg');
            onUpdate({ yAxis: { ...config.yAxis, metric, label: t(getMetricLabelKey(metric)), aggregation: newAgg } });
          }}
          options={yMetrics}
        />

        {/* Aggregation — only for numeric Y metrics, hidden for boxplot */}
        {showAggregationSelect && (
          <div className="col-span-2">
            <LabeledSelect
              label={t('analytics.dashboard.complexCharts.configuration.aggregation')}
              value={config.yAxis.aggregation ?? (yMetricDef?.defaultAggregation ?? 'avg')}
              onChange={(v) => {
                onUpdate({ yAxis: { ...config.yAxis, aggregation: v as AggregationKey } });
              }}
              options={aggregationOptions}
            />
          </div>
        )}

        {/* Aggregation Level */}
        {showAggLevel && (
          <AggregationLevelSelector
            value={config.aggregatedBy}
            onChange={(value) => onUpdate({ aggregatedBy: value })}
            mode={mode}
          />
        )}
      </div>

      {/* Registry-driven option fields for the current chart type */}
      {chartTypeDef && chartTypeDef.optionFields.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {chartTypeDef.optionFields.map((field) => (
            <OptionFieldRenderer
              key={field.key}
              field={field}
              options={config.options}
              onOptionsChange={(updates) =>
                onUpdate({ options: { ...config.options, ...updates } })
              }
            />
          ))}
        </div>
      )}

      {/* Advanced Options Panel */}
      <div className="border-t pt-4">
        <button
          type="button"
          aria-expanded={showAdvanced}
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex w-full items-center justify-between rounded-sm text-sm font-medium outline-none hover:text-primary focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <span>{t('analytics.dashboard.complexCharts.configuration.advancedOptions')}</span>
          {showAdvanced ? (
            <ChevronUp className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4" aria-hidden />
          )}
        </button>

        {showAdvanced && (
          <div className="mt-4 space-y-3">
            {['scatter', 'bar', 'histogram'].includes(config.type) && (
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={config.options.showGridLines ?? true}
                  onCheckedChange={(v) =>
                    onUpdate({ options: { ...config.options, showGridLines: v === true } })
                  }
                />
                <span className="text-sm">
                  {t('analytics.dashboard.complexCharts.configuration.showGridLines')}
                </span>
              </label>
            )}

            {/* Color By */}
            <LabeledSelect
              label={t('analytics.dashboard.complexCharts.configuration.colorBy')}
              value={config.options.colorBy || 'toolId'}
              onChange={(value) =>
                onUpdate({
                  options: { ...config.options, colorBy: value as 'toolId' | 'machine' | 'material' | 'reference' | 'toolState' },
                })
              }
              options={[
                { value: 'toolId', label: t('analytics.dashboard.complexCharts.configuration.metrics.toolId') },
                ...(mode === 'comparison'
                  ? [{ value: 'reference', label: t('analytics.dashboard.complexCharts.configuration.metrics.reference') }]
                  : []),
                { value: 'machine', label: t('analytics.dashboard.complexCharts.configuration.metrics.machine') },
                { value: 'material', label: t('analytics.dashboard.complexCharts.configuration.metrics.material') },
                { value: 'toolState', label: t('analytics.dashboard.complexCharts.configuration.metrics.toolState') },
              ]}
            />

          </div>
        )}
      </div>
    </div>
  );
}
